/**
 * EarningsSummary - Beautiful earnings summary display with markdown formatting
 */
import React from 'react';
import { YStack, XStack, H2, H3, Text, Separator } from 'tamagui';
import { GlassCard } from './base';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface EarningsSummaryProps {
  ticker: string;
  summary: string;
}

// Extract grade from the summary text
const extractGrade = (summary: string): string | null => {
  // Look for patterns like "**Overall Grade: X**" or "Overall Grade: X"
  const gradeMatch = summary.match(/\*\*Overall Grade:\s*([^*\n]+)\*\*/i) ||
                    summary.match(/Overall Grade:\s*([^\n]+)/i) ||
                    summary.match(/Grade:\s*\*\*([^*\n]+)\*\*/i) ||
                    summary.match(/Grade:\s*([^\n]+)/i);
  return gradeMatch ? gradeMatch[1].trim() : null;
};

// Get color based on grade
const getGradeColor = (grade: string): string => {
  const gradeLower = grade.toLowerCase();
  if (gradeLower.includes('incredible') || gradeLower.includes('a+') || gradeLower.includes('a-')) {
    return '$cyan10';
  }
  if (gradeLower.includes('good') || gradeLower.includes('b')) {
    return '$green10';
  }
  if (gradeLower.includes('neutral') || gradeLower.includes('c')) {
    return '$yellow10';
  }
  if (gradeLower.includes('bad') || gradeLower.includes('d')) {
    return '$orange10';
  }
  if (gradeLower.includes('horrible') || gradeLower.includes('f')) {
    return '$red10';
  }
  return '$color';
};

// Get background color for grade card
const getGradeBackgroundColor = (grade: string): string => {
  const gradeLower = grade.toLowerCase();
  if (gradeLower.includes('incredible') || gradeLower.includes('a+') || gradeLower.includes('a-')) {
    return '$cyan3';
  }
  if (gradeLower.includes('good') || gradeLower.includes('b')) {
    return '$green3';
  }
  if (gradeLower.includes('neutral') || gradeLower.includes('c')) {
    return '$yellow3';
  }
  if (gradeLower.includes('bad') || gradeLower.includes('d')) {
    return '$orange3';
  }
  if (gradeLower.includes('horrible') || gradeLower.includes('f')) {
    return '$red3';
  }
  return '$backgroundHover';
};

export const EarningsSummary: React.FC<EarningsSummaryProps> = ({ ticker, summary }) => {
  const grade = extractGrade(summary);

  return (
    <YStack gap="$4">
      {/* Grade Card */}
      {grade && (
        <GlassCard
          backgroundColor={getGradeBackgroundColor(grade)}
          borderColor={getGradeColor(grade)}
          borderWidth={2}
        >
          <YStack gap="$3" alignItems="center" paddingVertical="$4">
            <Text
              fontSize="$2"
              color="$placeholderColor"
              fontFamily="$mono"
              textTransform="uppercase"
              letterSpacing={2}
            >
              Earnings Grade
            </Text>
            <Text
              fontSize="$9"
              fontWeight="900"
              color={getGradeColor(grade)}
              fontFamily="$heading"
              letterSpacing={1}
            >
              {grade.toUpperCase()}
            </Text>
            <Text fontSize="$3" color="$colorPress" fontFamily="$mono">
              ${ticker}
            </Text>
          </YStack>
        </GlassCard>
      )}

      {/* Summary Card with Markdown */}
      <GlassCard>
        <YStack gap="$4">
          <XStack gap="$3" alignItems="center">
            <H2 color="$cyan10" fontFamily="$heading" flex={1} fontSize="$7">
              ${ticker} Earnings Analysis
            </H2>
          </XStack>

          <Separator borderColor="$borderColor" />

          {/* Markdown Content */}
          <YStack gap="$4" className="earnings-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Headings
                h1: ({ children }) => (
                  <H2 color="$cyan10" fontFamily="$heading" marginTop="$5" marginBottom="$3" fontSize="$6">
                    {children}
                  </H2>
                ),
                h2: ({ children }) => (
                  <H3 color="rgba(255, 255, 255, 0.95)" fontFamily="$heading" marginTop="$4" marginBottom="$3" fontSize="$5">
                    {children}
                  </H3>
                ),
                h3: ({ children }) => (
                  <Text fontSize="$5" fontWeight="600" color="rgba(255, 255, 255, 0.9)" fontFamily="$heading" marginTop="$3" marginBottom="$2">
                    {children}
                  </Text>
                ),
                // Paragraphs
                p: ({ children }) => (
                  <Text color="rgba(200, 200, 200, 0.9)" fontFamily="$body" fontSize="$4" lineHeight={24} marginBottom="$3">
                    {children}
                  </Text>
                ),
                // Strong/Bold
                strong: ({ children }) => (
                  <Text fontWeight="700" color="rgba(255, 255, 255, 0.95)" fontFamily="$heading">
                    {children}
                  </Text>
                ),
                // Lists
                ul: ({ children }) => (
                  <YStack gap="$2.5" marginLeft="$1" marginBottom="$4" paddingLeft="$3">
                    {children}
                  </YStack>
                ),
                ol: ({ children }) => (
                  <YStack gap="$2.5" marginLeft="$1" marginBottom="$4" paddingLeft="$3">
                    {children}
                  </YStack>
                ),
                li: ({ children }) => (
                  <XStack gap="$3" alignItems="flex-start">
                    <Text color="$cyan10" fontSize="$2" marginTop="$1.5">▸</Text>
                    <Text color="rgba(200, 200, 200, 0.9)" fontFamily="$body" fontSize="$4" flex={1} lineHeight={22}>
                      {children}
                    </Text>
                  </XStack>
                ),
                // Code
                code: ({ children }) => (
                  <Text
                    fontFamily="$mono"
                    fontSize="$3"
                    color="$cyan10"
                    backgroundColor="rgba(0, 255, 128, 0.05)"
                    paddingHorizontal="$2.5"
                    paddingVertical="$1.5"
                    borderRadius="$2"
                    borderWidth={1}
                    borderColor="rgba(0, 255, 128, 0.2)"
                  >
                    {children}
                  </Text>
                ),
                // Blockquote
                blockquote: ({ children }) => (
                  <YStack
                    borderLeftWidth={3}
                    borderLeftColor="$cyan10"
                    paddingLeft="$4"
                    marginVertical="$4"
                    backgroundColor="rgba(0, 255, 255, 0.03)"
                    paddingVertical="$3"
                    borderRadius="$2"
                  >
                    {children}
                  </YStack>
                ),
              }}
            >
              {summary}
            </ReactMarkdown>
          </YStack>
        </YStack>
      </GlassCard>
    </YStack>
  );
};

export default EarningsSummary;
